import React from 'react';
import { createPortal } from 'react-dom';
import TransferActionButton from './TransferActionButton';

export type TransferMenuItem = {
  key: string;
  label: string;
  onSelect: () => void;
  allowed: boolean;
  reason?: string | null;
  danger?: boolean;
};

type TransferMoreMenuProps = {
  items: ReadonlyArray<TransferMenuItem>;
  /** The trigger's accessible name, e.g. "More actions: add shipment, add stop, cancel transfer". */
  label: string;
  triggerClassName: string;
  busy?: boolean;
};

const MENU_WIDTH = 288;
const VIEWPORT_PAD = 8;

/**
 * The header's ⋯ menu. The header clips overflow, so the menu is portalled and placed under its
 * trigger. Arrow keys, Home and End move; Escape closes it (and only it) and returns focus to the
 * trigger. Items the server refuses stay listed, inert, with their reason. With one item there is
 * no menu: the item shows as a button.
 */
export default function TransferMoreMenu({ items, label, triggerClassName, busy = false }: TransferMoreMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [placement, setPlacement] = React.useState<React.CSSProperties | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const menuId = React.useId();

  const place = React.useCallback(function place() {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(MENU_WIDTH, viewportWidth - VIEWPORT_PAD * 2);
    const left = Math.min(Math.max(VIEWPORT_PAD, rect.right - width), viewportWidth - width - VIEWPORT_PAD);
    const below = viewportHeight - rect.bottom - VIEWPORT_PAD;
    const above = rect.top - VIEWPORT_PAD;
    const openUp = below < 220 && above > below;
    setPlacement(openUp
      ? { position: 'fixed', left, width, bottom: viewportHeight - rect.top + 4, maxHeight: above - 4 }
      : { position: 'fixed', left, width, top: rect.bottom + 4, maxHeight: below - 4 });
  }, []);

  // Focus goes back to the trigger at once, so a dialog an item opens returns focus there.
  const close = React.useCallback(function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const handleMove = () => place();
    window.addEventListener('resize', handleMove);
    window.addEventListener('scroll', handleMove, true);
    return () => {
      window.removeEventListener('resize', handleMove);
      window.removeEventListener('scroll', handleMove, true);
    };
  }, [open, place]);

  React.useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      first?.focus({ preventScroll: true });
    });
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open]);

  if (!items.length) return null;
  if (items.length === 1) {
    const [only] = items;
    return (
      <TransferActionButton
        className={`${triggerClassName} transfer-more-menu__single${only.danger ? ' transfer-more-menu__single--danger' : ''}`}
        allowed={only.allowed}
        reason={only.reason}
        busy={busy}
        onClick={only.onSelect}
      >
        {only.label}
      </TransferActionButton>
    );
  }

  function handleTriggerClick() {
    setOpen((value) => !value);
  }

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function handleMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const entries = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const index = entries.indexOf(document.activeElement as HTMLElement);
    const focusAt = (next: number) => entries[(next + entries.length) % entries.length]?.focus({ preventScroll: true });
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(entries.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        // Only the menu closes; the record header and dialogs underneath must not see it.
        event.stopPropagation();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  }

  function choose(item: TransferMenuItem) {
    if (!item.allowed || busy) return;
    close(true);
    item.onSelect();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${triggerClassName} transfer-more-menu__trigger`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        <svg className="transfer-more-menu__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open && placement && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="transfer-more-menu"
            role="menu"
            aria-label={label}
            style={placement}
            onKeyDown={handleMenuKeyDown}
          >
            {items.map((item) => (
              <TransferMenuEntry key={item.key} item={item} busy={busy} onChoose={choose} />
            ))}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}

function TransferMenuEntry({
  item,
  busy,
  onChoose,
}: {
  item: TransferMenuItem;
  busy: boolean;
  onChoose: (item: TransferMenuItem) => void;
}) {
  const reasonId = React.useId();
  const inert = !item.allowed || busy;
  const reason = !item.allowed ? item.reason || 'Not available right now.' : null;
  function handleClick() {
    onChoose(item);
  }
  return (
    <button
      type="button"
      role="menuitem"
      className={`transfer-more-menu__item${item.danger ? ' transfer-more-menu__item--danger' : ''}`}
      aria-disabled={inert || undefined}
      aria-describedby={reason ? reasonId : undefined}
      tabIndex={-1}
      onClick={handleClick}
    >
      <span className="transfer-more-menu__label">{item.label}</span>
      {reason ? <span id={reasonId} className="transfer-more-menu__reason">{reason}</span> : null}
    </button>
  );
}
